import { i18n } from '@/i18n';
import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { formOptionsAggregateBusiness, formOptionsBusiness, inputTypeKeyMap, ModalType, validateRuleKeyMap } from './constants';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { ExistsMasterDataResponseData } from '@/coreInfrastructureModule/axios/generated/registerBusinessManage';
import { diContainer } from '@/coreContainer/container';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { SortType } from '@/coreInfrastructureModule/axios/generated/inventoryManage';
import { FilterContentItem } from '../filterModal/filterModal';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';

export const useUpdateBusinessModalStore = defineStore('updateBusinessModal', () => {
  // region Variables
  const t = i18n.t;

  const state = reactive<IUpdateBusinessModalState>(initState());

  // #endregion

  // #region PublicMethods
  /**
   * stateをリセットする
   */
  const $reset = () => {
    Object.assign(state, initState());
  };

  /**
   * 新規追加可能な項目のリストを取得する関数
   * @param isAggregateBusiness - 集計かどうか
   * @returns - 新規追加可能な項目のリスト
   */
  const getAddableOptions = (isAggregateBusiness?: boolean) => {
    if (state.isCreatingBoth && isAggregateBusiness) {
      return [t('manufacturer')];
    }
    const targetFormOptions = isAggregateBusiness ? formOptionsAggregateBusiness : formOptionsBusiness;
    const res = [...targetFormOptions].filter((option) => {
      return inputTypeKeyMap[option] === 'select';
    });

    return res.map((r) => t(r));
  };

  /**
   * APIに集計編集を投げる関数
   */
  const putEditedAggregateBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (
          checkDuplicationResponse.status.data.aggregateBusinessId &&
          checkDuplicationResponse.status.data.aggregateBusinessId !== updatedItem.businessId
        ) {
          // 重複がある
          return 'duplicateError';
        } else {
          // 重複がないもしくは同一IDのものが重複している（＝キー以外の項目のみ編集している）
          const putResponse = await diContainer.putAggregateBusinessUseCase.execute(updatedItem);
          if (putResponse.status.type === 'success') {
            return 'success';
          }
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  /**
   * APIに関連取引編集を投げる関数
   */
  const putEditedBaseBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateBaseBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (
          checkDuplicationResponse.status.data.baseBusinessId &&
          checkDuplicationResponse.status.data.baseBusinessId !== updatedItem.businessId
        ) {
          return 'duplicateError';
        } else if (checkDuplicationResponse.status.data.aggregateBusinessId) {
          // 重複がないもしくは同一IDのものが重複している（＝キー以外の項目のみ編集している）かつ、aggregateBusinessIdがある（集計が存在する）
          const putResponse = await diContainer.putBaseBusinessUseCase.execute(updatedItem);
          if (putResponse.status.type === 'success') {
            return 'success';
          }
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  /**
   * APIに集計作成を投げる関数
   */
  const createNewAggregateBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (checkDuplicationResponse.status.data.aggregateBusinessId) {
          // 重複がある
          return 'duplicateError';
        } else {
          // 重複がない
          const postResponse = await diContainer.createNewAggregateBusinessUseCase.Execute(updatedItem);
          if (postResponse.status.type === 'success') {
            return 'success';
          }
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  /**
   * APIに関連取引作成を投げる関数
   */
  const createNewBaseBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateBaseBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (checkDuplicationResponse.status.data.baseBusinessId) {
          // 重複がある
          return 'duplicateError';
        } else if (checkDuplicationResponse.status.data.aggregateBusinessId) {
          // 重複がないかつ、aggregateBusinessIdがある（集計が存在する）

          const parentId = checkDuplicationResponse.status.data.aggregateBusinessId;

          const postResponse = await diContainer.createNewBaseBusinessUseCase.Execute(updatedItem, parentId);
          if (postResponse.status.type === 'success') {
            return 'success';
          }
        } else {
          return 'noParentError';
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  // =================== 新規項目追加関連 ===================

  /**
   * 新規追加中の選択肢の項目をstateに保存する処理
   * @param option - ユーザーが選択した選択肢の項目
   */
  const setNewOptionItemType = (option: string) => {
    state.newOption.itemType = _convertNewOptionToItemType(option) ?? '';
  };

  /**
   * 項目内容の新規追加時に、対となる項目が存在する場合にそれを返す関数
   * @param item - 現在追加している項目
   * @returns - 対となる項目、ない場合はundefined
   */
  const getAnotherAddingItem = (item: string) => {
    if (item === t('package') || item === t('manufacturer') || item === t('endUser')) return undefined;
    switch (item) {
      case t('warehouseNumber'):
        return t('warehouseName');
      case t('warehouseName'):
        return t('warehouseNumber');
      case t('supplierNumber'):
        return t('supplierName');
      case t('supplierName'):
        return t('supplierNumber');
      case t('businessNumber'):
        return t('businessName');
      case t('businessName'):
        return t('businessNumber');
      case t('customerNumber'):
        return t('customerName');
      case t('customerName'):
        return t('customerNumber');
      case t('salesDestinationNumber'):
        return t('salesDestinationName');
      case t('salesDestinationName'):
        return t('salesDestinationNumber');
    }
  };

  /**
   * 新規に追加しようとする選択肢が重複していないかを確認する処理
   * ManufacturerとEndUserは重複チェックしない
   * @param value - 新規追加するvalue(warehouseName, supplierName, etc...)
   * @param code - 新規追加するCode(warehouseNumber, supplierNumber, etc...)
   */
  const checkDuplicationOfNewOption = async (value: string, code: string): Promise<ExistsMasterDataResponseData | null> => {
    if (state.newOption.itemType === 'manufacturer' || state.newOption.itemType === 'endUser') {
      // ManufacturerとEndUserは重複チェックせず、existsResultをfalseにして返す
      return {
        existsResult: false,
        existsCode: '',
        existsValue: '',
      };
    }
    if (!state.newOption.itemType) {
      console.error('エラー:取引編集>新規選択肢追加:追加したい項目がStateに存在しません');
      return null;
    }
    const response = await diContainer.checkNewOptionDuplicateUseCase.execute(state.newOption.itemType, code, value);
    switch (response.status.type) {
      case 'success':
        return response.status.data;
      case 'error':
      default:
        console.error('エラー:取引編集>新規選択肢追加:サーバーエラー');
        return null;
    }
  };

  // =================== 集計子取引 ===================
  /**
   * 集計行の子取引を取得する処理
   * @param sortType - ソートの向き
   * @param sortTarget - ソートの対象カラム
   */
  const fetchChildBusiness = async (sortType: SortType = 'ASC', sortTarget = 'baseBusinessId') => {
    state.childBusiness.loadingState.status.type = 'loading';
    const response = await diContainer.getEditBusinessChildBusinessUseCase.execute(
      sortType,
      sortTarget,
      state.childBusiness.pagingState.pageSize,
      state.childBusiness.pagingState.offset,
      _getChildBusinessFilterItem()
    );
    switch (response.status.type) {
      case 'success':
        state.childBusiness.tableContentItems = _addAggregateBusinessInfoToChildBusiness(response.status.data.result);
        state.childBusiness.pagingState = response.status.data.pagingOptions;
        // 取得した情報数が0の場合、ゼロマッチステータスにする
        if (state.childBusiness.tableContentItems.length === 0) {
          state.childBusiness.loadingState.status.type = 'zeroMatch';
        } else {
          state.childBusiness.loadingState.status.type = 'success';
        }
        break;
      case 'error':
      default:
        state.childBusiness.loadingState.status.type = 'error';
        return null;
    }
  };

  // #endregion

  // #region Private methods
  /**
   * 新規選択肢追加で追加する項目を重複チェックAPI用に変換する関数
   * @param option - 追加する項目
   * @returns - 何を重複チェックするかのparameter
   */
  const _convertNewOptionToItemType = (option: string) => {
    switch (option) {
      case t('warehouseNumber'):
      case t('warehouseName'):
        return 'warehouse';
      case t('supplierNumber'):
      case t('supplierName'):
        return 'supplier';
      case t('businessNumber'):
      case t('businessName'):
        return 'item';
      case t('package'):
        return 'package';
      case t('manufacturer'):
        return 'manufacturer';
      case t('customerNumber'):
      case t('customerName'):
        return 'customer';
      case t('salesDestinationNumber'):
      case t('salesDestinationName'):
        return 'salesDestination';
      case t('endUser'):
        return 'endUser';
      default:
        return '';
    }
  };

  /**
   * 小取引に集計の情報を付加する処理
   * @param businesses - 子取引の一覧
   */
  const _addAggregateBusinessInfoToChildBusiness = (businesses: IBusiness[]) => {
    return businesses.map(
      (business): IBusiness => ({
        ...business,
        warehouse: state.item?.warehouse ?? '',
        supplierName: state.item?.supplierName ?? '',
        businessNumber: state.item?.businessNumber ?? '',
        businessName: state.item?.businessName ?? '',
        package: state.item?.package ?? '',
      })
    );
  };

  /**
   * 子取引取得時のフィルターを作成して返す
   */
  const _getChildBusinessFilterItem = (): FilterContentItem[] => {
    // フィルター項目は親の必須項目
    return [
      {
        filterType: 'WAREHOUSE_CODE',
        selectedValue: [state.updatedItemDiff?.warehouseNumber ?? state.item?.warehouseNumber ?? ''],
        label: '',
      },
      {
        filterType: 'SUPPLIER_CODE',
        selectedValue: [state.updatedItemDiff?.supplierNumber ?? state.item?.supplierNumber ?? ''],
        label: '',
      },
      {
        filterType: 'BUSINESS_CODE',
        selectedValue: [state.updatedItemDiff?.businessNumber ?? state.item?.businessNumber ?? ''],
        label: '',
      },
      {
        filterType: 'PACKAGE_AMOUNT',
        selectedValue: [state.updatedItemDiff?.package ?? state.item?.package ?? ''],
        label: '',
      },
    ];
  };
  // #endregion

  return {
    state,
    $reset,
    getAddableOptions,
    putEditedAggregateBusiness,
    putEditedBaseBusiness,
    createNewAggregateBusiness,
    createNewBaseBusiness,
    setNewOptionItemType,
    getAnotherAddingItem,
    checkDuplicationOfNewOption,
    fetchChildBusiness,
  };
});

// #region Interface

/**
 * updateBusinessModalStateのinterface
 * @property item - 編集中の取引情報
 * @property updatedItemDiff - 編集した取引の差分
 * @property isCreateMode - 新規作成かどうか
 * @property isCreatingBoth - 関連取引の新規作成時に集計も新規作成しているかどうか
 * @property modalState - モーダルの開閉状態
 * @property childBusiness - 集計を編集した際に影響を受ける子取引に関する状態
 * @property newOption - 新規選択肢追加に関する状態
 */
interface IUpdateBusinessModalState {
  item: IBusiness | undefined;
  updatedItemDiff: Partial<IBusiness> | undefined;
  isCreateMode: boolean;
  isCreatingBoth: boolean;
  modalState: {
    aggregateBusinessEdit: boolean;
    baseBusinessEdit: boolean;
  };
  childBusiness: {
    loadingState: LoadingState;
    pagingState: IPagingOptions;
    tableContentItems: any[];
  };
  newOption: {
    itemType: string;
    isAnotherInputShow: boolean;
  };
}

/**
 * UpdateBusinessModalStateの初期値を返す
 * @returns - Stateの初期値
 */
const initState = (): IUpdateBusinessModalState => {
  return {
    item: undefined,
    updatedItemDiff: undefined,
    isCreateMode: false,
    isCreatingBoth: false,
    modalState: {
      aggregateBusinessEdit: false,
      baseBusinessEdit: false,
    },
    childBusiness: {
      loadingState: {
        status: {
          type: 'none',
        },
      },
      pagingState: initPagingOptions(50),
      tableContentItems: [],
    },
    newOption: {
      itemType: '',
      isAnotherInputShow: false,
    },
  };
};

// endregion
